<template>
  <div
    v-if="$store.state.locale.enabled"
    class="ui-locale-switcher"
    :class="{ 'is-compact': compact, 'is-bare': bare }"
  >
    <!-- 语言 -->
    <button
      ref="trigger"
      type="button"
      class="locale-trigger"
      :aria-label="$t('common.language')"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-controls="menuId"
      @click="open = !open"
    >
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="9" />
        <path
          d="M3 12h18M12 3c2.3 2.45 3.45 5.45 3.45 9S14.3 18.55 12 21M12 3C9.7 5.45 8.55 8.45 8.55 12S9.7 18.55 12 21"
        />
      </svg>
      <span v-if="!compact && !bare">{{ currentLabel }}</span>
    </button>
    <!-- 语言 -->
    <div
      v-if="open"
      :id="menuId"
      class="locale-menu"
      :style="menuStyle"
      role="group"
      :aria-label="$t('common.language')"
    >
      <button
        v-for="option in options"
        :key="option.locale"
        type="button"
        :lang="option.locale"
        :disabled="!option.enabled"
        :aria-pressed="uiLocale === option.locale ? 'true' : 'false'"
        :class="{ 'is-selected': uiLocale === option.locale }"
        @click="chooseLocale(option.locale)"
      >
        <span>{{ option.label }}</span>
        <!-- 即将开放 -->
        <span v-if="!option.enabled" class="locale-pending">{{
          $t("account.comingSoon")
        }}</span>
        <span v-else-if="uiLocale === option.locale" aria-hidden="true">✓</span>
      </button>
    </div>
  </div>
</template>

<script>
import { languageOptions } from "@/i18n/common/index.js";

export default {
  name: "LocaleSwitcher",
  props: {
    compact: { type: Boolean, default: false },
    bare: { type: Boolean, default: false },
    menuStyle: { type: Object, default: () => ({}) },
  },
  data() {
    return { open: false, options: languageOptions };
  },
  computed: {
    uiLocale() {
      return this.$store.state.locale.uiLocale;
    },
    currentLabel() {
      const option = this.options.find((item) => item.locale === this.uiLocale);
      return option ? option.label : "简体中文";
    },
    menuId() {
      return `ui-locale-menu-${this._uid}`;
    },
  },
  mounted() {
    document.addEventListener("pointerdown", this.closeOutside);
    document.addEventListener("keydown", this.closeOnEscape);
  },
  beforeDestroy() {
    document.removeEventListener("pointerdown", this.closeOutside);
    document.removeEventListener("keydown", this.closeOnEscape);
  },
  methods: {
    chooseLocale(locale) {
      const option = this.options.find((item) => item.locale === locale);
      if (!option || !option.enabled) return;
      // 仅切换显示文案，不加载业务数据或刷新页面。
      if (locale !== this.uiLocale) {
        this.$store.dispatch("locale/chooseUiLocale", locale);
      }
      this.open = false;
      this.$refs.trigger.focus();
    },
    closeOutside(event) {
      if (!this.$el.contains(event.target)) this.open = false;
    },
    closeOnEscape(event) {
      if (event.key !== "Escape" || !this.open) return;
      this.open = false;
      this.$refs.trigger.focus();
    },
  },
};
</script>

<style lang="less" scoped>
.ui-locale-switcher {
  position: relative;
  display: inline-flex;
  align-items: center;
  min-width: 44px;

  .locale-trigger {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-width: 44px;
    min-height: 44px;
    padding: 0 12px;
    border: 1px solid #dde3ee;
    border-radius: 8px;
    background: #fff;
    color: #334155;
    font: inherit;
    cursor: pointer;

    svg {
      width: 22px;
      height: 22px;
      fill: none;
      stroke: currentColor;
      stroke-width: 1.7;
    }

    &:focus-visible {
      outline: 2px solid #4286f5;
      outline-offset: 2px;
    }
  }

  &.is-compact .locale-trigger {
    padding: 0;
    border-color: transparent;
    border-radius: 50%;
    background: transparent;

    &:hover {
      background: #f8fafc;
    }
  }

  &.is-bare .locale-trigger {
    padding: 0;
    border: 0;
    border-radius: 0;
    background: transparent;
    box-shadow: none;

    &:hover { background: transparent; }
  }

  .locale-menu {
    position: absolute;
    top: calc(100% + 4px);
    right: 0;
    z-index: 500;
    width: 232px;
    max-width: calc(100vw - 32px);
    padding: 8px;
    border: 1px solid #dde3ee;
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);

    button {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      width: 100%;
      min-height: 44px;
      padding: 8px 12px;
      border: 0;
      border-radius: 8px;
      background: transparent;
      color: #334155;
      font: inherit;
      text-align: left;
      cursor: pointer;

      &.is-selected,
      &:hover:not(:disabled) {
        background: #f2f7ff;
        color: #246de0;
      }

      &:focus-visible {
        outline: 2px solid #4286f5;
      }

      &:disabled {
        color: #667085;
        cursor: not-allowed;
      }
    }

    .locale-pending {
      font-size: 12px;
    }
  }
}
</style>
